import { DocumentServicesObject } from '@wix/document-services-types';
import { getUxDataWithDefaults } from '@wix/ambassador-dm-editor-extensions-v1-ux-data/http';
import { HttpClient } from '@wix/http-client';
import type {
  ProvidersServiceAPI,
  DataBindingServiceAPI,
} from '@wix/editor-platform-feature-store';
import type { EditorPlatformPanoramaClientAPI } from '@wix/editor-platform-feature-store/internal';

import { PlatformManifestService } from '../PlatformManifestService';
import { DependencyToken } from '../../dependencies';
import { HostAPI } from '../../types';
import { ClientSpecMapService } from '../../sharedServices';

import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

export class EditorEnvironment {
  #httpClient = new HttpClient();

  constructor(
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private dm: DocumentServicesObject,
    private readonly hostAPI: HostAPI,
    private readonly clientSpecMapService: ClientSpecMapService,
    private readonly panoramaService: EditorPlatformPanoramaClientAPI,
    private readonly providersAPI?: ProvidersServiceAPI,
    private readonly bindingsAPI?: DataBindingServiceAPI,
  ) {}

  setDependenciesMethods(platformManifestService: PlatformManifestService) {
    const dependenciesContributor =
      platformManifestService.dependenciesContributor;

    // Contribute all external APIs
    dependenciesContributor.set(
      DependencyToken.UX_DATA_GET_WITH_DEFAULTS,
      async ({ componentType, language }) => {
        const { data } = await this.#httpClient.request(
          getUxDataWithDefaults({
            uxDataId: componentType,
            language,
          }),
        );

        return data.uxData?.fields ?? {};
      },
    );

    dependenciesContributor.set(
      DependencyToken.BUILDER_DATA_UPDATE,
      (editorPointer, data) => {
        this.hostAPI.components.data.setData(editorPointer, data);
      },
    );

    dependenciesContributor.set(
      DependencyToken.BUILDER_DATA_GET,
      (editorPointer) => {
        return this.hostAPI.components.data.getData(editorPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.OOI_DATA_GET,
      (editorPointer, key, cb) => {
        this.hostAPI.tpa.data.comp.get(editorPointer, key, cb);
      },
    );

    dependenciesContributor.set(
      DependencyToken.OOI_DATA_UPDATE,
      (editorPointer, data) => {
        this.hostAPI.tpa.data.setMultiple(editorPointer, data, 'COMPONENT');
      },
    );

    // Styles
    dependenciesContributor.set(
      DependencyToken.BUILDER_STYLES_GET,
      (editorPointer, options) => {
        return (
          this.hostAPI.components.cssStyle.get(editorPointer, options) ?? {
            cssProperties: {},
            cssCustomProperties: {},
          }
        );
      },
    );

    dependenciesContributor.set(
      DependencyToken.BUILDER_STYLES_UPDATE,
      (editorPointer, styles) => {
        this.hostAPI.components.cssStyle.update(editorPointer, styles);
      },
    );

    dependenciesContributor.set(
      DependencyToken.BUILDER_STYLES_REMOVE,
      (editorPointer, styles) => {
        /**
         * restore host api usage
         * as soon as the editor implements the function
         * https://github.com/wix-private/responsive-editor-packages/blob/master/packages/editor-package-platform-proxy/src/document/components/createDocumentComponentsNamespaceAPI.ts#L485
         */
        // this.hostAPI.components.cssStyle.remove(editorPointer, _styles);
        const innerElementPath =
          this.editorPointerAPI.getInnerElementPath(editorPointer);

        if (innerElementPath?.length) {
          this.dm.components.innerElements.cssStyle.remove(
            this.editorPointerAPI.getDocumentPointer(editorPointer),
            innerElementPath,
            styles,
          );
        } else {
          this.dm.components.cssStyle.remove(
            this.editorPointerAPI.getDocumentPointer(editorPointer),
            styles,
          );
        }
      },
    );

    dependenciesContributor.set(
      DependencyToken.OOI_STYLE_GET_EFFECTIVE,
      (editorPointer) => {
        return this.hostAPI.tpa.style.getEffective(editorPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.OOI_STYLE_GET,
      (editorPointer) => {
        return this.hostAPI.tpa.style.getNonEffective(editorPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.OOI_STYLE_UPDATE,
      (editorPointer, values) => {
        this.hostAPI.tpa.style.setWithoutSideEffects(editorPointer, values);
      },
    );

    // Stage
    dependenciesContributor.set(
      DependencyToken.STAGE_BREAKPOINT_GET,
      (editorPointer) => {
        return this.hostAPI.stage.getBreakpointFromPointer(editorPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.STAGE_BREAKPOINT_POINTER_GET,
      (editorPointer, breakpoint) => {
        return this.hostAPI.stage.getPointerWithBreakpointVariant(
          editorPointer,
          breakpoint,
        );
      },
    );

    // Props
    dependenciesContributor.set(
      DependencyToken.PROPS_REMOVE,
      (path, propPath) => {
        this.dm.components.runtime.removeProps(path, propPath);
      },
    );

    dependenciesContributor.set(DependencyToken.PROPS_UPDATE, (path, props) => {
      this.dm.components.runtime.updateProps(path, props);
    });

    dependenciesContributor.set(DependencyToken.PROPS_GET, (path) => {
      return this.dm.components.runtime.getProps(path) ?? {};
    });

    dependenciesContributor.set(
      DependencyToken.PROPS_OVERRIDES_UPDATE,
      (editorPointer, propsToUpdate) => {
        const documentPointer =
          this.editorPointerAPI.getDocumentPointer(editorPointer);
        const innerElementPath =
          this.editorPointerAPI.getInnerElementPath(editorPointer);
        if (innerElementPath?.length) {
          this.dm.components.innerElements.propsOverrides.update(
            documentPointer,
            innerElementPath,
            propsToUpdate,
          );
        } else {
          this.dm.components.runtime.updatePropsOverrides(
            documentPointer,
            propsToUpdate,
          );
        }
      },
    );

    dependenciesContributor.set(
      DependencyToken.PROPS_OVERRIDES_GET,
      (editorPointer) => {
        const documentPointer =
          this.editorPointerAPI.getDocumentPointer(editorPointer);
        const innerElementPath =
          this.editorPointerAPI.getInnerElementPath(editorPointer);
        if (innerElementPath?.length) {
          return this.dm.components.innerElements.propsOverrides.get(
            documentPointer,
            innerElementPath,
          );
        }
        return this.dm.components.runtime.getPropsOverrides(documentPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.PROPS_OVERRIDES_REMOVE,
      (editorPointer, keys) => {
        const documentPointer =
          this.editorPointerAPI.getDocumentPointer(editorPointer);
        const innerElementPath =
          this.editorPointerAPI.getInnerElementPath(editorPointer);
        if (innerElementPath?.length) {
          this.dm.components.innerElements.propsOverrides.remove(
            documentPointer,
            innerElementPath,
            keys,
          );
        } else {
          this.dm.components.runtime.removePropsOverrides(
            documentPointer,
            keys,
          );
        }
      },
    );

    dependenciesContributor.set(DependencyToken.DM_CHANGES_WAIT, () => {
      return this.dm.waitForChangesAppliedAsync();
    });

    // Layout
    dependenciesContributor.set(
      DependencyToken.LAYOUT_COMPONENT_UPDATE,
      (params) => {
        this.hostAPI.editor.updateComponentLayout?.(params);
      },
    );

    dependenciesContributor.set(
      DependencyToken.LAYOUT_CONTAINER_REGENERATE,
      (params) => {
        this.hostAPI.editor.regenerateContainerLayout?.(params);
      },
    );

    // State
    dependenciesContributor.set(
      DependencyToken.VIEW_STATE_GET,
      (componentRef) => {
        return this.hostAPI.viewState.getViewState(componentRef) || null;
      },
    );

    dependenciesContributor.set(
      DependencyToken.VIEW_STATE_UPDATE,
      (componentRef, state) => {
        this.hostAPI.viewState.setViewState(componentRef, state);
      },
    );

    dependenciesContributor.set(
      DependencyToken.DM_STAGE_DATA_GET,
      (componentRef) => {
        return this.dm.platform.controllers.getStageData(componentRef);
      },
    );

    // Design States
    dependenciesContributor.set(
      DependencyToken.DESIGN_STATES_GET,
      (editorPointers) => {
        return this.hostAPI.editor.states.getDesignStates(editorPointers);
      },
    );

    dependenciesContributor.set(
      DependencyToken.DESIGN_STATES_SET,
      (editorPointers, states) => {
        this.hostAPI.editor.states.setDesignStates(editorPointers, states);
      },
    );

    dependenciesContributor.set(
      DependencyToken.DESIGN_STATES_RESET,
      (editorPointers) => {
        this.hostAPI.editor.states.resetDesignStates(editorPointers);
      },
    );

    // Presets
    dependenciesContributor.set(
      DependencyToken.BUILDER_PRESETS_GET_EFFECTIVE,
      (editorPointer) => {
        return this.hostAPI.components.presets.getEffective(editorPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.BUILDER_PRESETS_GET,
      (editorPointer) => {
        return this.hostAPI.components.presets.get(editorPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.BUILDER_PRESETS_UPDATE,
      (editorPointer, presetName) => {
        this.hostAPI.components.presets.set(editorPointer, presetName);
      },
    );

    // Resolved Data
    dependenciesContributor.set(DependencyToken.DM_SVG_INFO_GET, (uri) => {
      return this.dm.media.vectorImage.getSvgInfoFromCache(uri);
    });

    // Client spec map
    dependenciesContributor.set(
      DependencyToken.COMP_TYPE_GET,
      (editorPointer) => {
        return this.clientSpecMapService.getCompType(editorPointer);
      },
    );

    // Components
    dependenciesContributor.set(
      DependencyToken.COMPONENTS_CONTAINER_GET,
      (documentPointer) => {
        return this.dm.components.getContainer(documentPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.COMPONENTS_CHILDREN_GET,
      (documentPointer) => {
        return this.dm.components.getChildren(documentPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.COMPONENTS_SLOTS_GET_NAMES,
      (documentPointer) => {
        return this.dm.components.slots.getSlotNames(documentPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.COMPONENTS_SLOTS_GET_DATA,
      (documentPointer) => {
        return this.dm.components.slots.getSlotsData(documentPointer);
      },
    );

    dependenciesContributor.set(
      DependencyToken.COMPONENTS_SLOTS_BUILDER_GET_DATA,
      (documentPointer) => {
        return this.dm.components.slots.builder.getPopulatedComponentContainerInfoMap(
          documentPointer,
        );
      },
    );

    // Providers
    if (this.providersAPI) {
      dependenciesContributor.set(
        DependencyToken.PROVIDERS_LIST,
        this.providersAPI.list.bind(this.providersAPI),
      );

      dependenciesContributor.set(
        DependencyToken.PROVIDERS_ATTACH,
        this.providersAPI.attach.bind(this.providersAPI),
      );

      dependenciesContributor.set(
        DependencyToken.PROVIDERS_DETACH,
        this.providersAPI.detach.bind(this.providersAPI),
      );

      dependenciesContributor.set(
        DependencyToken.PROVIDERS_GET_DATA,
        this.providersAPI.getData.bind(this.providersAPI),
      );

      dependenciesContributor.set(
        DependencyToken.PROVIDERS_UPDATE_DATA,
        this.providersAPI.updateData.bind(this.providersAPI),
      );
    }

    // Bindings
    if (this.bindingsAPI) {
      dependenciesContributor.set(
        DependencyToken.BINDINGS_BIND,
        this.bindingsAPI.bind.bind(this.bindingsAPI),
      );

      dependenciesContributor.set(
        DependencyToken.BINDINGS_SET_BINDING,
        this.bindingsAPI.setBinding.bind(this.bindingsAPI),
      );

      dependenciesContributor.set(
        DependencyToken.BINDINGS_UNBIND,
        this.bindingsAPI.unbind.bind(this.bindingsAPI),
      );

      dependenciesContributor.set(
        DependencyToken.BINDINGS_LIST,
        this.bindingsAPI.listBindings.bind(this.bindingsAPI),
      );

      dependenciesContributor.set(
        DependencyToken.BINDINGS_GET_BINDING,
        this.bindingsAPI.getBinding.bind(this.bindingsAPI),
      );

      dependenciesContributor.set(
        DependencyToken.BINDINGS_FIND_BINDING_CANDIDATES,
        this.bindingsAPI.findBindingCandidates.bind(this.bindingsAPI),
      );
    }

    // Panorama
    dependenciesContributor.set(
      DependencyToken.PANORAMA_TRACK_TRANSACTION,
      this.panoramaService?.trackTransactionSync.bind(this.panoramaService),
    );
  }
}
